/**
 * PilotBadge + PilotFeedbackForm — Powersure controlled pilot UX.
 *
 * Renders only when the loaded intake is flagged is_pilot=true.
 * Pure additive — does not block the normal flow or hide commerce CTAs.
 *
 * Hard rules:
 *  - No PII shown.
 *  - No automatic email or checkout.
 *  - Feedback POST is gated by backend (pilot intake required).
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, FlaskConical, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PilotBadge({ pilotType }) {
  if (!pilotType) return null;
  const label = pilotType === 'mkb' ? 'MKB pilot' : 'Particulier pilot';
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full bg-amber-100 border border-amber-300 text-amber-900 px-3 py-1 text-xs font-semibold"
      data-testid="pilot-badge"
      data-pilot-type={pilotType}
    >
      <FlaskConical className="w-3.5 h-3.5" />
      Pilot toegang — {label} · onbetaalde testgebruiker
    </div>
  );
}

export default function PilotFeedbackForm({ intakeId, pilotType }) {
  const [form, setForm] = useState({
    first_impression: '',
    intake_clear: '',
    advice_logical: '',
    would_consider_ec: '',
    would_request_quote: '',
    missing: '',
    score: '',
    may_contact: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!intakeId) return null;

  const onChange = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        intake_id: intakeId,
        first_impression: form.first_impression,
        intake_clear: form.intake_clear,
        advice_logical: form.advice_logical,
        would_consider_ec: form.would_consider_ec,
        would_request_quote: form.would_request_quote,
        missing: form.missing,
        score: form.score ? Number(form.score) : null,
        may_contact: !!form.may_contact,
      };
      await axios.post(`${API}/pilot/feedback`, payload);
      setSubmitted(true);
      toast.success('Bedankt — feedback opgeslagen');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
        data-testid="pilot-feedback-thanks"
      >
        <div className="flex items-center gap-2 text-emerald-900 font-semibold">
          <CheckCircle2 className="w-5 h-5" />
          Bedankt voor je feedback!
        </div>
        <p className="text-sm text-emerald-900/80 mt-1">
          Powersure leest pilotfeedback handmatig — geen automatische opvolging.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5"
      data-testid="pilot-feedback-form"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-amber-900 mb-1">
        <FlaskConical className="w-4 h-4" />
        Pilot feedback {pilotType === 'mkb' ? '· MKB' : '· Particulier'}
      </div>
      <h3 className="text-xl font-medium text-amber-950" style={{ fontFamily: 'Outfit' }}>
        Help Powersure dit beter te maken
      </h3>
      <p className="text-sm text-amber-900/80 mt-1">
        Je bent een pilotgebruiker. Je antwoorden helpen ons om intake en advies aan te scherpen.
      </p>

      <form onSubmit={submit} className="mt-4 grid grid-cols-1 gap-3">
        {[
          ['first_impression', 'Wat is je eerste indruk?'],
          ['intake_clear', 'Was de intake duidelijk?'],
          ['advice_logical', 'Was het advies logisch?'],
          ['would_consider_ec', 'Zou je Energy Control overwegen?'],
          ['would_request_quote', 'Zou je een productpakket/offerte aanvragen?'],
          ['missing', 'Wat miste je?'],
        ].map(([k, label]) => (
          <label key={k} className="text-sm">
            <span className="block text-amber-900 mb-1">{label}</span>
            <textarea
              value={form[k]}
              onChange={onChange(k)}
              rows={2}
              maxLength={1000}
              className="w-full rounded-md border border-amber-300 px-2 py-1.5 text-sm bg-white"
              data-testid={`pilot-fb-${k}`}
            />
          </label>
        ))}
        <label className="text-sm flex items-center gap-2">
          <span className="text-amber-900">Score (1–10)</span>
          <input
            type="number"
            min={1}
            max={10}
            value={form.score}
            onChange={onChange('score')}
            className="w-20 rounded-md border border-amber-300 px-2 py-1 text-sm bg-white"
            data-testid="pilot-fb-score"
          />
        </label>
        <label className="text-sm flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.may_contact}
            onChange={onChange('may_contact')}
            data-testid="pilot-fb-may-contact"
          />
          <span className="text-amber-900">Powersure mag contact opnemen voor vervolg</span>
        </label>
        <div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-semibold bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60"
            data-testid="pilot-fb-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Verstuur pilot feedback
          </button>
        </div>
      </form>
    </section>
  );
}
